import React, {useState} from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material'
import {LocalizationProvider, DatePicker} from '@mui/x-date-pickers'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { RootState } from '../../../../setup'
import { useSelector } from 'react-redux'
const selectTimeZone = (state: RootState) => state.timeZone.timeZone
interface Goal {
  shortTermObjectives: string
  projectedDate: string
  dateAchieved: string
}

interface SpeechTherapyDialogProps {
  goalRecords: Goal[]
  setGoalRecords: (records: Goal[]) => void
  open: boolean
  onOpen: () => void
  onClose: () => void
  isDisabled: boolean
}

const SpeechTherapyDialog: React.FC<SpeechTherapyDialogProps> = ({
  goalRecords,
  setGoalRecords,
  open,
  onOpen,
  onClose,
  isDisabled = false,
}) => {
  const [newGoal, setNewGoal] = useState<Goal>({
    shortTermObjectives: '',
    projectedDate: '',
    dateAchieved: '',
  })
  const [showWarning, setShowWarning] = useState(false)
  const timeZone = useSelector(selectTimeZone)
  const handleAddPerson = () => {
    if (!newGoal.shortTermObjectives || !newGoal.projectedDate) {
      setShowWarning(true)
      return
    }

    setGoalRecords([...goalRecords, newGoal])
    setNewGoal({
      shortTermObjectives: '',
      projectedDate: '',
      dateAchieved: '',
    })
    onClose()
  }

  const handleDeletePerson = (index: number) => {
    const updated = goalRecords.filter((_, i) => i !== index)
    setGoalRecords(updated)
  }

  const handleInputChange = (field: keyof Goal, value: any) => {
    setNewGoal((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleCloseWarning = () => {
    setShowWarning(false)
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead style={{backgroundColor: '#2f3347'}}>
            <TableRow>
              <TableCell style={{color: 'white'}}>Actions</TableCell>
              <TableCell style={{color: 'white'}}>Short Term Objective</TableCell>
              <TableCell style={{color: 'white'}}>Projected Date</TableCell>
              <TableCell style={{color: 'white'}}>Date Achieved</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {goalRecords.length > 0 ? (
              goalRecords.map((person, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <IconButton disabled={isDisabled} onClick={() => handleDeletePerson(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>{person.shortTermObjectives}</TableCell>
                  <TableCell>
                      {person.projectedDate
                        ? dayjs.utc(person.projectedDate).tz(timeZone, true).format('MM-DD-YYYY')
                        : '--'}
                    </TableCell>
                    <TableCell>
                      {person.dateAchieved
                        ? dayjs.utc(person.dateAchieved).tz(timeZone, true).format('MM-DD-YYYY')
                        : '--'}
                    </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align='center'
                  style={{fontWeight: 'bold', fontSize: '16px'}}
                >
                  No Records Added
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Button disabled={isDisabled} variant='contained' onClick={onOpen} startIcon={<AddIcon />} sx={{mt: 2}}>
        Add Goal
      </Button>

      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle>Add Goal</DialogTitle>
        <DialogContent sx={{pt: 2}}>
          <TextField
            label='Short Term Objective'
            fullWidth
            value={newGoal.shortTermObjectives}
            onChange={(e) => handleInputChange('shortTermObjectives', e.target.value)}
            multiline
            rows={3}
            sx={{mb: 2, mt: 1}}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label='Projected Date'
              value={newGoal.projectedDate ? dayjs(newGoal.projectedDate) : null}
              onChange={(newValue) =>
                newValue && handleInputChange('projectedDate', newValue.format('MM-DD-YYYY'))
              }
              slotProps={{
                field:{
                  readOnly: true,
                },
                textField: {fullWidth: true, sx: {mb: 2},
                inputProps: { 
                  style: { cursor: 'pointer' },
                  readOnly: true,
                },
                onClick: (e) => {
                  // Prevent the click from bubbling up and trigger the date picker
                  e.preventDefault();
                  e.stopPropagation();
                  // Find and click the calendar icon to open the picker
                  const iconButton = e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') || 
                                   e.currentTarget.querySelector('.MuiIconButton-root');
                  if (iconButton) {
                    (iconButton as HTMLElement).click();
                  }
                }}
              }}
            />
            <DatePicker
              label='Date Achieved'
              value={newGoal.dateAchieved ? dayjs(newGoal.dateAchieved) : null}
              onChange={(newValue) =>
                newValue && handleInputChange('dateAchieved', newValue.format('MM-DD-YYYY'))
              }
              slotProps={{
                field:{
                  readOnly: true,
                },
                textField: {fullWidth: true,
                  inputProps: { 
                    style: { cursor: 'pointer' },
                    readOnly: true,
                  },
                  onClick: (e) => {
                    // Prevent the click from bubbling up and trigger the date picker
                    e.preventDefault();
                    e.stopPropagation();
                    // Find and click the calendar icon to open the picker
                    const iconButton = e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') || 
                                     e.currentTarget.querySelector('.MuiIconButton-root');
                    if (iconButton) {
                      (iconButton as HTMLElement).click();
                    }
                  }
                },
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant='contained' onClick={handleAddPerson}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showWarning}
        autoHideDuration={4000}
        onClose={handleCloseWarning}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
      >
        <Alert onClose={handleCloseWarning} severity='warning' sx={{width: '100%'}}>
          Please fill in the Short Term Objective and Projected Date.
        </Alert>
      </Snackbar>
    </>
  )
}

export default SpeechTherapyDialog
